"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReconciledBusTapeResultSchema = exports.ReconciledMsrValuesSchema = exports.ReconcileCheckSchema = exports.ReconcileCheckStatusSchema = exports.ReconcileStatusSchema = exports.ReconciledMsrRowSchema = exports.ReconciledDataTapeRowArraySchema = exports.ReconciledDataTapeRowSchema = void 0;
const zod_1 = require("zod");
/**
 * Backup Servicing (BUS) reconcile step — canonical schemas.
 *
 * Single source of truth for the reconcile step's inputs and output, shared by:
 *  - the `createReconciledBusTape` Fargate task (parses the sanitized tapes at
 *    runtime, and builds/validates the result JSON before upload), and
 *  - the `reconciled-bus-tape` tape-builder uinode (derives the required input
 *    column names from these schemas' keys for graph-author-time validation).
 *
 * Keeping the canonical column names as the object keys here means the
 * author-time column check and the runtime parse can never drift apart.
 */
/**
 * A tape value that must be numeric. Sanitized tape cells arrive as either a
 * number or a numeric string; a missing (null/undefined) or non-numeric value
 * is a hard error (surfaced by the parse) rather than a silent zero.
 */
const numericTapeField = zod_1.z
    .union([zod_1.z.number(), zod_1.z.string()])
    .transform((value, ctx) => {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: `expected a numeric value, got "${String(value)}"`,
        });
        return zod_1.z.NEVER;
    }
    return parsed;
});
/**
 * A tape value that must be present and is read as a string (dates arrive as
 * either an ISO string or a serial number depending on the source file).
 */
const stringTapeField = zod_1.z
    .union([zod_1.z.string(), zod_1.z.number()])
    .transform((value) => String(value));
/**
 * A LENIENT numeric MSR cell: a blank (null / empty) or non-numeric value maps
 * to `null` (→ a skipped check) instead of erroring. The MSR is a fixed-layout
 * form whose cells don't line up perfectly across every period, and the tie-out
 * requirements are still firming up, so a missing cell must NOT fail the run —
 * it's recorded as unverifiable. Contrast `numericTapeField` (data tape), where
 * a missing value is still a hard error.
 */
const nullableNumericTapeField = zod_1.z
    .union([zod_1.z.number(), zod_1.z.string()])
    .nullable()
    .transform((value) => {
    if (value === null || value === '') {
        return null;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
});
/** A LENIENT string MSR cell: blank (null / empty) maps to `null`. */
const nullableStringTapeField = zod_1.z
    .union([zod_1.z.string(), zod_1.z.number()])
    .nullable()
    .transform((value) => (value === null || value === '' ? null : String(value)));
// ── inputs ──────────────────────────────────────────────────────────────────
/** Per-row shape of the sanitized data tape (only the fields reconcile reads). */
exports.ReconciledDataTapeRowSchema = zod_1.z.object({
    current_principal_bal: numericTapeField,
    as_of_date: stringTapeField,
});
exports.ReconciledDataTapeRowArraySchema = zod_1.z.array(exports.ReconciledDataTapeRowSchema);
/**
 * Single-row shape of the extracted MSR control values. Every cell is LENIENT
 * (nullable): a blank/garbage cell parses to `null` and downstream becomes a
 * skipped check, never a hard parse failure. See `nullableNumericTapeField`.
 */
exports.ReconciledMsrRowSchema = zod_1.z.object({
    collection_period_ending: nullableStringTapeField,
    beginning_pool_balance: nullableNumericTapeField,
    ending_pool_balance: nullableNumericTapeField,
    ending_pool_balance_footing: nullableNumericTapeField,
    servicing_fee: nullableNumericTapeField,
});
// ── output ──────────────────────────────────────────────────────────────────
exports.ReconcileStatusSchema = zod_1.z.enum(['pass', 'fail', 'skipped']);
/**
 * Outcome of a single tie-out check. `skipped` means the check could not run
 * (its MSR input cell was blank/absent) — distinct from `fail`. This keeps the
 * checks flexible while the reconcile requirements settle: everything is
 * surfaced (in the result JSON + the cycle row's `variances`), nothing errors.
 */
exports.ReconcileCheckStatusSchema = zod_1.z.enum(['pass', 'fail', 'skipped']);
exports.ReconcileCheckSchema = zod_1.z.object({
    name: zod_1.z.string(),
    status: exports.ReconcileCheckStatusSchema,
    detail: zod_1.z.string(),
});
/**
 * The MSR control values echoed into the result. `null` at the top level when no
 * MSR is wired; individual fields are also nullable (a blank cell in the form).
 */
exports.ReconciledMsrValuesSchema = zod_1.z.object({
    collectionPeriodEnding: zod_1.z.string().nullable(),
    beginningPoolBalance: zod_1.z.number().nullable(),
    endingPoolBalance: zod_1.z.number().nullable(),
    endingPoolBalanceFooting: zod_1.z.number().nullable(),
    servicingFee: zod_1.z.number().nullable(),
});
/**
 * The reconcile result JSON written to S3 and read by the certificate step.
 * `status` is `'skipped'` when no checks ran (no MSR tape wired) — distinct
 * from a `'pass'` where checks ran and all passed.
 *
 * `cycleId` back-references the `bus_servicing_cycle` DB row the reconcile task
 * upserts (the committed system-of-record the certificate reads). It is the
 * other half of a bidirectional link: the DB row holds this artifact's S3 key
 * (`reconcile_result_ref`) and this JSON holds the row id. Always set — the task
 * always upserts a cycle row and embeds its id — so it is a required non-null
 * string (the whole stack lands together, so no window exists where the schema
 * requires a key the task doesn't yet provide).
 */
exports.ReconciledBusTapeResultSchema = zod_1.z.object({
    status: exports.ReconcileStatusSchema,
    reportingPeriod: zod_1.z.string().nullable(),
    controlTotals: zod_1.z.object({
        loanCount: zod_1.z.number(),
        totalPrincipal: zod_1.z.number(),
    }),
    msrValues: exports.ReconciledMsrValuesSchema.nullable(),
    checks: zod_1.z.array(exports.ReconcileCheckSchema),
    provenance: zod_1.z.string(),
    cycleId: zod_1.z.string(),
});
//# sourceMappingURL=ReconciledBusTape.js.map